import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  MagnifyingGlassIcon,
  TruckIcon,
  ChartBarIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  PlusCircleIcon,
  MapPinIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  BookOpenIcon,
  ScaleIcon,
  BuildingOfficeIcon,
  InformationCircleIcon,
  StarIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'
import { useAuth } from '../../../contextos/autenticacion/ContextoAutenticacion'

// Interfaces para tipado
interface ConsultaLiga {
  id: string
  titulo: string
  descripcion: string
  icono: React.ComponentType<any>
  color: string
  gradiente: string
  acciones: {
    titulo: string
    descripcion: string
    ruta: string
    icono: React.ComponentType<any>
  }[]
}

interface AccesoRapido {
  id: string
  titulo: string
  descripcion: string
  icono: React.ComponentType<any>
  color: string
  ruta: string
  destacado?: boolean
}

interface NotificacionSistema {
  id: string
  tipo: 'info' | 'warning' | 'success' | 'update'
  titulo: string
  mensaje: string
  tiempo: string
  icono: React.ComponentType<any>
  color: string
}

const PaginaHomeUsuario: React.FC = () => {
  const { usuario } = useAuth()
  const [notificaciones, setNotificaciones] = useState<NotificacionSistema[]>([])
  const [cargando, setCargando] = useState(true)

  // Datos de consultas a la Liga
  const consultasLiga: ConsultaLiga[] = [
    {
      id: 'normativas',
      titulo: 'Consultas Normativas',
      descripcion: 'Accede a leyes, tratados y documentos normativos actualizados',
      icono: DocumentTextIcon,
      color: 'blue',
      gradiente: 'from-blue-500 to-blue-600',
      acciones: [
        {
          titulo: 'Leyes y Tratados',
          descripcion: 'Consulta legislación vigente',
          ruta: '/consultas/leyes',
          icono: ScaleIcon
        },
        {
          titulo: 'Documentos Normativos',
          descripcion: 'Accede a circulares y boletines',
          ruta: '/consultas/documentos',
          icono: DocumentTextIcon
        },
        {
          titulo: 'Búsqueda Avanzada',
          descripcion: 'Encuentra información específica',
          ruta: '/consultas/busqueda',
          icono: MagnifyingGlassIcon
        }
      ]
    },
    {
      id: 'tarifas',
      titulo: 'Tarifas LIGIE',
      descripcion: 'Consulta tarifas arancelarias y clasificaciones de mercancías',
      icono: GlobeAltIcon,
      color: 'green',
      gradiente: 'from-green-500 to-green-600',
      acciones: [
        {
          titulo: 'Clasificación Arancelaria',
          descripcion: 'Busca códigos LIGIE',
          ruta: '/tarifas/clasificacion',
          icono: BookOpenIcon
        },
        {
          titulo: 'Calculadora de Aranceles',
          descripcion: 'Calcula impuestos y derechos',
          ruta: '/tarifas/calculadora',
          icono: ChartBarIcon
        },
        {
          titulo: 'INCOTERMS',
          descripcion: 'Consulta términos comerciales',
          ruta: '/tarifas/incoterms',
          icono: GlobeAltIcon
        }
      ]
    }
  ]

  // Accesos rápidos
  const accesosRapidos: AccesoRapido[] = [
    {
      id: 'crear-envio',
      titulo: 'Crear Nuevo Envío',
      descripcion: 'Registra un envío rápidamente',
      icono: PlusCircleIcon,
      color: 'blue',
      ruta: '/envios/crear',
      destacado: true
    },
    {
      id: 'seguimiento',
      titulo: 'Seguimiento de Envíos',
      descripcion: 'Rastrea tus envíos en tiempo real',
      icono: MapPinIcon,
      color: 'green',
      ruta: '/envios/seguimiento'
    },
    {
      id: 'reportes',
      titulo: 'Reportes y Estadísticas',
      descripcion: 'Analiza el rendimiento',
      icono: ChartBarIcon,
      color: 'purple',
      ruta: '/reportes'
    },
    {
      id: 'flota',
      titulo: 'Gestión de Flota',
      descripcion: 'Administra vehículos y conductores',
      icono: TruckIcon,
      color: 'orange',
      ruta: '/flota'
    }
  ]

  // Cargar datos
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        // Simular carga de datos
        await new Promise(resolve => setTimeout(resolve, 800))
        
        setNotificaciones([
          {
            id: '1',
            tipo: 'info',
            titulo: 'Nueva actualización disponible',
            mensaje: 'Se han actualizado las tarifas LIGIE para el mes actual',
            tiempo: 'Hace 1 hora',
            icono: InformationCircleIcon,
            color: 'blue'
          },
          {
            id: '2',
            tipo: 'success',
            titulo: 'Sistema optimizado',
            mensaje: 'Las consultas ahora son 30% más rápidas',
            tiempo: 'Hace 3 horas',
            icono: CheckCircleIcon,
            color: 'green'
          },
          {
            id: '3',
            tipo: 'update',
            titulo: 'Nuevas funcionalidades',
            mensaje: 'Disponible calculadora de aranceles mejorada',
            tiempo: 'Hace 1 día',
            icono: StarIcon,
            color: 'purple'
          }
        ])

        setCargando(false)
      } catch (error) {
        console.error('Error al cargar datos:', error)
        setCargando(false)
      }
    }

    cargarDatos()
  }, [])

  const obtenerColorClase = (color: string, tipo: 'bg' | 'text' | 'border' | 'bg-light' | 'hover-bg' = 'bg') => {
    const colores = {
      blue: { 
        bg: 'bg-blue-500 dark:bg-blue-600', 
        'bg-light': 'bg-blue-100 dark:bg-blue-900/20',
        'hover-bg': 'hover:bg-blue-50 dark:hover:bg-blue-900/20',
        text: 'text-blue-600 dark:text-blue-400', 
        border: 'border-blue-200 dark:border-blue-700' 
      },
      green: { 
        bg: 'bg-green-500 dark:bg-green-600', 
        'bg-light': 'bg-green-100 dark:bg-green-900/20',
        'hover-bg': 'hover:bg-green-50 dark:hover:bg-green-900/20',
        text: 'text-green-600 dark:text-green-400', 
        border: 'border-green-200 dark:border-green-700' 
      },
      orange: { 
        bg: 'bg-orange-500 dark:bg-orange-600', 
        'bg-light': 'bg-orange-100 dark:bg-orange-900/20',
        'hover-bg': 'hover:bg-orange-50 dark:hover:bg-orange-900/20',
        text: 'text-orange-600 dark:text-orange-400', 
        border: 'border-orange-200 dark:border-orange-700' 
      },
      purple: { 
        bg: 'bg-purple-500 dark:bg-purple-600', 
        'bg-light': 'bg-purple-100 dark:bg-purple-900/20',
        'hover-bg': 'hover:bg-purple-50 dark:hover:bg-purple-900/20',
        text: 'text-purple-600 dark:text-purple-400', 
        border: 'border-purple-200 dark:border-purple-700' 
      },
      red: { 
        bg: 'bg-red-500 dark:bg-red-600', 
        'bg-light': 'bg-red-100 dark:bg-red-900/20',
        'hover-bg': 'hover:bg-red-50 dark:hover:bg-red-900/20',
        text: 'text-red-600 dark:text-red-400', 
        border: 'border-red-200 dark:border-red-700' 
      }
    }
    return colores[color as keyof typeof colores]?.[tipo] || colores.blue[tipo]
  }

  if (cargando) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center transition-colors duration-200">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-2">Cargando...</h2>
          <p className="text-gray-600 dark:text-gray-400">Preparando tu espacio de trabajo</p>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* Encabezado de bienvenida */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 text-white rounded-2xl p-8 mb-8 shadow-xl overflow-hidden relative"
        >
          {/* Elementos decorativos */}
          <div className="absolute inset-0 overflow-hidden opacity-20">
            <div className="absolute -right-10 -top-10 w-32 h-32 rounded-full bg-white/10"></div>
            <div className="absolute left-1/4 -bottom-16 w-64 h-64 rounded-full bg-white/10"></div>
          </div>
          
          <div className="relative z-10">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
              <div className="mb-6 md:mb-0">
                <motion.h1 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-4xl md:text-5xl font-bold mb-4"
                >
                  Centro de Control
                </motion.h1>
                <motion.p 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                  className="text-xl text-purple-100"
                >
                  Tu hub central para consultas y gestión logística
                </motion.p>
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 }}
                  className="mt-4 flex items-center space-x-4"
                >
                  <div className="flex items-center space-x-2">
                    <BuildingOfficeIcon className="h-5 w-5" />
                    <span className="text-purple-100">{usuario?.empresa || 'Tu Empresa'}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <ClockIcon className="h-5 w-5" />
                    <span className="text-purple-100">
                      {new Date().toLocaleDateString('es-ES', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </span>
                  </div>
                </motion.div>
              </div>
              <motion.div 
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.8 }}
                className="hidden lg:block"
              >
                <GlobeAltIcon className="h-24 w-24 opacity-30" />
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Accesos rápidos mejorados */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-lg">
                <ArrowTrendingUpIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">
                  Accesos Rápidos
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Herramientas más utilizadas
                </p>
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl transition-all duration-200 text-sm font-medium"
            >
              Personalizar
            </motion.button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
            {accesosRapidos.map((acceso, index) => (
              <motion.a
                key={acceso.id}
                href={acceso.ruta}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                whileHover={{ y: -8, scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`group relative bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-2xl dark:shadow-gray-900/20 transition-all duration-300 border border-gray-100 dark:border-gray-700 overflow-hidden ${
                  acceso.destacado ? 'ring-2 ring-blue-500 ring-opacity-50' : ''
                }`}
              >
                {/* Fondo decorativo */}
                <div className={`absolute inset-0 bg-gradient-to-br ${
                  acceso.color === 'blue' ? 'from-blue-500/5 to-blue-600/5' :
                  acceso.color === 'green' ? 'from-green-500/5 to-green-600/5' :
                  acceso.color === 'orange' ? 'from-orange-500/5 to-orange-600/5' :
                  acceso.color === 'purple' ? 'from-purple-500/5 to-purple-600/5' :
                  'from-gray-500/5 to-gray-600/5'
                } opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                
                <div className="relative">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`p-3 rounded-xl bg-gradient-to-r ${
                      acceso.color === 'blue' ? 'from-blue-500 to-blue-600' :
                      acceso.color === 'green' ? 'from-green-500 to-green-600' :
                      acceso.color === 'orange' ? 'from-orange-500 to-orange-600' :
                      acceso.color === 'purple' ? 'from-purple-500 to-purple-600' :
                      'from-gray-500 to-gray-600'
                    } shadow-lg group-hover:shadow-xl transition-shadow duration-300`}>
                      <acceso.icono className="h-6 w-6 text-white" />
                    </div>
                    {acceso.destacado && (
                      <motion.div
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="bg-yellow-100 dark:bg-yellow-900/20 p-1 rounded-full"
                      >
                        <StarIcon className="h-4 w-4 text-yellow-500" />
                      </motion.div>
                    )}
                  </div>
                  
                  <h3 className="font-bold text-gray-800 dark:text-gray-200 mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">
                    {acceso.titulo}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    {acceso.descripcion}
                  </p>
                  
                  {/* Indicador de acción */}
                  <div className="mt-4 flex items-center justify-between">
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Hacer clic para acceder
                    </div>
                    <motion.div
                      initial={{ x: 0 }}
                      animate={{ x: [0, 4, 0] }}
                      transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                      className="text-gray-400 group-hover:text-blue-500 transition-colors duration-300"
                    >
                      →
                    </motion.div>
                  </div>
                </div>
                
                {/* Efecto de brillo en hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-700" />
              </motion.a>
            ))}
          </div>
        </motion.div>

        {/* Consultas a la Liga */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-8"
        >
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-6 flex items-center">
            <MagnifyingGlassIcon className="h-6 w-6 text-green-500 mr-3" />
            Consultas a la Liga
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {consultasLiga.map((consulta, index) => (
              <motion.div
                key={consulta.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                whileHover={{ y: -8 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-xl dark:shadow-gray-900/20 transition-all duration-300 overflow-hidden"
              >
                <div className={`bg-gradient-to-r ${consulta.gradiente} p-6 text-white`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold mb-2">{consulta.titulo}</h3>
                      <p className="text-white/80 text-sm">{consulta.descripcion}</p>
                    </div>
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <consulta.icono className="h-8 w-8 opacity-80" />
                    </motion.div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="space-y-3">
                    {consulta.acciones.map((accion) => (
                      <motion.a
                        key={accion.titulo}
                        href={accion.ruta}
                        whileHover={{ x: 5 }}
                        className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 group"
                      >
                        <div className="flex items-center space-x-3">
                          <accion.icono className={`h-5 w-5 ${obtenerColorClase(consulta.color, 'text')}`} />
                          <div>
                            <span className="font-medium text-gray-800 dark:text-gray-200">{accion.titulo}</span>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{accion.descripcion}</p>
                          </div>
                        </div>
                        <motion.div
                          initial={{ x: 0 }}
                          whileHover={{ x: 5 }}
                          transition={{ duration: 0.2 }}
                        >
                          <svg className="h-5 w-5 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </motion.div>
                      </motion.a>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Notificaciones del sistema */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg dark:shadow-gray-900/20 p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200 flex items-center">
              <InformationCircleIcon className="h-6 w-6 text-blue-500 mr-3" />
              Notificaciones del Sistema
            </h3>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="text-blue-600 hover:text-blue-700 transition-colors duration-200 text-sm font-medium"
            >
              Ver todas
            </motion.button>
          </div>
          
          <div className="space-y-4">
            <AnimatePresence>
              {notificaciones.map((notificacion, index) => (
                <motion.div
                  key={notificacion.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ delay: 0.1 * index }}
                  whileHover={{ x: 5 }}
                  className="flex items-start space-x-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-200 cursor-pointer"
                >
                  <div className={`${obtenerColorClase(notificacion.color, 'bg')} bg-opacity-10 p-2 rounded-full flex-shrink-0`}>
                    <notificacion.icono className={`h-5 w-5 ${obtenerColorClase(notificacion.color, 'text')}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 dark:text-gray-200">{notificacion.titulo}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{notificacion.mensaje}</p>
                    <span className="text-xs text-gray-500 dark:text-gray-400 mt-2 block">{notificacion.tiempo}</span>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default PaginaHomeUsuario